import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Download, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function AdminSubscribers() {
  const { user, logout } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/subscribers");
      setSubs(r.data || []);
    } catch {
      toast.error("Could not load subscribers.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    if (subs.length === 0) return toast.error("No subscribers to export yet.");
    const rows = [["Email", "Source", "Subscribed"], ...subs.map((s) => [s.email, s.source || "", s.createdAt || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("Subscriber list exported.");
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

  return (
    <div className="min-h-screen bg-linen">
      <Seo title="Subscribers — Let Go Wellness Admin" description="Your newsletter subscriber list." path="/admin/subscribers" />
      <header className="sticky top-0 z-20 border-b border-border bg-linen/85 backdrop-blur-lg">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-6">
            <div className="font-serif text-xl text-ink">Let Go <span className="italic text-clay">Wellness</span> · Admin</div>
            <nav className="flex gap-1">
              <Link to="/admin" className="rounded-full px-4 py-1.5 font-sans text-xs text-ink-soft transition-colors hover:bg-white">Submissions</Link>
              <Link to="/admin/ai" className="rounded-full px-4 py-1.5 font-sans text-xs text-ink-soft transition-colors hover:bg-white">AI Studio</Link>
              <Link to="/admin/subscribers" className="rounded-full bg-ink px-4 py-1.5 font-sans text-xs text-linen">Subscribers</Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-sans text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 font-sans text-xs text-ink-soft transition-colors hover:bg-white"><LogOut size={13} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-ink">Subscribers</h1>
            <p className="mt-2 font-sans text-sm text-muted-foreground" data-testid="subs-count">{subs.length} {subs.length === 1 ? "person" : "people"} on your Journal list</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 font-sans text-xs text-ink-soft transition-colors hover:bg-linen"><RefreshCw size={13} /> Refresh</button>
            <button data-testid="subs-export" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 font-sans text-xs text-linen transition-colors hover:bg-ink/85"><Download size={13} /> Export CSV</button>
          </div>
        </div>

        {loading ? (
          <p className="font-sans text-sm text-muted-foreground">Loading…</p>
        ) : subs.length === 0 ? (
          <div className="border border-border bg-white p-16 text-center font-sans text-sm text-muted-foreground" data-testid="subs-empty">No subscribers yet — they'll appear here when readers sign up on your blog.</div>
        ) : (
          <div className="space-y-px overflow-hidden border border-border bg-border" data-testid="subs-list">
            {subs.map((s) => (
              <div key={s.email} data-testid={`sub-row-${s.email}`} className="flex flex-wrap items-center justify-between gap-3 bg-white px-6 py-4">
                <a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 font-sans text-sm text-ink hover:text-clay"><Mail size={14} className="text-clay" />{s.email}</a>
                <span className="font-sans text-xs text-muted-foreground">Joined {fmt(s.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
